/* eslint-disable camelcase */

import { httpGet, httpPost, httpPut, httpDelete } from 'utils';
import { AppDispatch } from 'types';
import { openFlashMessage } from 'components/utils/flashMessages';
import { openPageLoading, closePageLoading } from 'containers/App/actions/ui';
import { setUpAndJoinDefaultChannels } from 'containers/App/actions/sessions';
import { handlePostLoginRedirect, clearAuthAndRedirect } from 'utils/auth';

import {
  MFA_SETUP_REQUEST,
  MFA_SETUP_SUCCESS,
  MFA_SETUP_ERROR,
  MFA_VERIFY_REQUEST,
  MFA_VERIFY_SUCCESS,
  MFA_VERIFY_ERROR,
  MFA_STATUS_REQUEST,
  MFA_STATUS_SUCCESS,
  MFA_STATUS_ERROR,
  MFA_DISABLE_REQUEST,
  MFA_DISABLE_SUCCESS,
  MFA_DISABLE_ERROR,
  MFA_REGENERATE_BACKUP_CODES_REQUEST,
  MFA_REGENERATE_BACKUP_CODES_SUCCESS,
  MFA_REGENERATE_BACKUP_CODES_ERROR,
  MFA_LOGIN_SUCCESS,
  MFA_LOGIN_ERROR,
  MFA_IN_PROGRESS_END,
  MFA_ENFORCEMENT_FETCH_REQUEST,
  MFA_ENFORCEMENT_FETCH_SUCCESS,
  MFA_ENFORCEMENT_FETCH_ERROR,
  MFA_ENFORCEMENT_UPDATE_REQUEST,
  MFA_ENFORCEMENT_UPDATE_SUCCESS,
  MFA_ENFORCEMENT_UPDATE_ERROR,
  MFA_TRUSTED_DEVICES_FETCH_REQUEST,
  MFA_TRUSTED_DEVICES_FETCH_SUCCESS,
  MFA_TRUSTED_DEVICES_FETCH_ERROR,
  MFA_TRUSTED_DEVICE_REVOKE_REQUEST,
  MFA_TRUSTED_DEVICE_REVOKE_SUCCESS,
  MFA_TRUSTED_DEVICE_REVOKE_ERROR,
  MFA_TRUSTED_DEVICES_REVOKE_ALL_REQUEST,
  MFA_TRUSTED_DEVICES_REVOKE_ALL_SUCCESS,
  MFA_TRUSTED_DEVICES_REVOKE_ALL_ERROR,
  MFA_USER_RESET_REQUEST,
  MFA_USER_RESET_SUCCESS,
  MFA_USER_RESET_ERROR,
} from './constants';
import { FlashDefinition } from './utils/flashDefinition';

type MFASetupResponse = {
  qr_code: string;
  secret: string;
  formatted_secret: string;
};

type MFAVerifyResponse = {
  enabled: boolean;
  method: string;
  backup_codes: string[];
  jwt?: string;
  exp?: string;
};

type MFAChallengeResponse = {
  jwt: string;
  exp: string;
  role: string;
  user: CurrentUser;
  company: CurrentCompany;
  extra_data?: {
    mfa_setup_required?: boolean;
    trusted_device_token?: string;
  };
};

export function setupMFA() {
  return (dispatch: AppDispatch) =>
    new Promise<MFASetupResponse>((resolve, reject) => {
      dispatch({ type: MFA_SETUP_REQUEST });

      httpPost('/mfa/setup', {})
        .then((response: MFASetupResponse) => {
          dispatch({ type: MFA_SETUP_SUCCESS, payload: response });
          resolve(response);
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_SETUP_ERROR });
          openFlashMessage(FlashDefinition.setupError);
          reject(error);
        });
    });
}

export function verifyMFA(code: string, secret?: string) {
  return (dispatch: AppDispatch) =>
    new Promise<MFAVerifyResponse>((resolve, reject) => {
      dispatch({ type: MFA_VERIFY_REQUEST });

      const payload = { code, ...(secret && { secret }) };

      httpPost('/mfa/verify', payload)
        .then((response: MFAVerifyResponse) => {
          if (response.jwt && response.exp) {
            localStorage.setItem('phoenixAuthToken', response.jwt);
            localStorage.setItem('phoenixExpToken', response.exp);
          }

          dispatch({ type: MFA_VERIFY_SUCCESS, payload: response });
          openFlashMessage(FlashDefinition.setupSuccess);
          resolve(response);
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_VERIFY_ERROR });
          openFlashMessage(FlashDefinition.invalidCode);
          reject(error);
        });
    });
}

export function getMFAStatus() {
  return (dispatch: AppDispatch) =>
    new Promise<MFAStatus>((resolve, reject) => {
      dispatch({ type: MFA_STATUS_REQUEST });

      httpGet('/mfa/status')
        .then((response: MFAStatus) => {
          dispatch({ type: MFA_STATUS_SUCCESS, payload: response });
          resolve(response);
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_STATUS_ERROR, error });
          reject(error);
        });
    });
}

export function disableMFA(password: string) {
  return (dispatch: AppDispatch) =>
    new Promise<void>((resolve, reject) => {
      dispatch({ type: MFA_DISABLE_REQUEST });

      httpPost('/mfa/disable', { password })
        .then(() => {
          dispatch({ type: MFA_DISABLE_SUCCESS });
          openFlashMessage(FlashDefinition.disableSuccess);
          resolve();
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_DISABLE_ERROR });

          if (error.response?.data?.error === 'mfa_enforcement_active') {
            openFlashMessage(FlashDefinition.disableEnforcementActive);
          } else if (error.response?.status === 401) {
            openFlashMessage(FlashDefinition.incorrectPassword);
          } else if (error.response?.status === 422) {
            openFlashMessage(FlashDefinition.disablePasswordRequired);
          } else {
            openFlashMessage(FlashDefinition.disableError);
          }

          reject(error);
        });
    });
}

export function regenerateBackupCodes(code: string) {
  return (dispatch: AppDispatch) =>
    new Promise<{ backup_codes: string[] }>((resolve, reject) => {
      dispatch({ type: MFA_REGENERATE_BACKUP_CODES_REQUEST });

      httpPost('/mfa/backup-codes/regenerate', { code })
        .then((response: { backup_codes: string[] }) => {
          dispatch({
            type: MFA_REGENERATE_BACKUP_CODES_SUCCESS,
            payload: { backup_codes: response.backup_codes },
          });
          openFlashMessage(FlashDefinition.backupCodesRegenerated);
          resolve(response);
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_REGENERATE_BACKUP_CODES_ERROR });
          openFlashMessage(FlashDefinition.invalidCode);
          reject(error);
        });
    });
}

export function fetchEnforcementStatus() {
  return (dispatch: AppDispatch) =>
    new Promise<MFAEnforcementStatus>((resolve, reject) => {
      dispatch({ type: MFA_ENFORCEMENT_FETCH_REQUEST });

      httpGet('/mfa/enforcement')
        .then((response: MFAEnforcementStatus) => {
          dispatch({
            type: MFA_ENFORCEMENT_FETCH_SUCCESS,
            payload: response,
          });
          resolve(response);
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_ENFORCEMENT_FETCH_ERROR, error });
          reject(error);
        });
    });
}

export function updateEnforcement(enforced: boolean) {
  return (dispatch: AppDispatch) =>
    new Promise<MFAEnforcementStatus>((resolve, reject) => {
      dispatch({ type: MFA_ENFORCEMENT_UPDATE_REQUEST });

      httpPut('/mfa/enforcement', { enforced })
        .then((response: MFAEnforcementStatus) => {
          dispatch({
            type: MFA_ENFORCEMENT_UPDATE_SUCCESS,
            payload: response,
          });
          openFlashMessage(
            enforced
              ? FlashDefinition.enforcementEnabled
              : FlashDefinition.enforcementDisabled
          );
          resolve(response);
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_ENFORCEMENT_UPDATE_ERROR });
          openFlashMessage(
            error.response?.data?.error === 'owner_mfa_not_enabled'
              ? FlashDefinition.ownerMfaRequired
              : FlashDefinition.enforcementUpdateError
          );
          reject(error);
        });
    });
}

export function fetchTrustedDevices() {
  return (dispatch: AppDispatch) =>
    new Promise<TrustedDevice[]>((resolve, reject) => {
      dispatch({ type: MFA_TRUSTED_DEVICES_FETCH_REQUEST });

      httpGet('/mfa/trusted-devices')
        .then((response: { trusted_devices: TrustedDevice[] }) => {
          dispatch({
            type: MFA_TRUSTED_DEVICES_FETCH_SUCCESS,
            payload: response.trusted_devices,
          });
          resolve(response.trusted_devices);
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_TRUSTED_DEVICES_FETCH_ERROR });
          openFlashMessage(FlashDefinition.trustedDevicesLoadError);
          reject(error);
        });
    });
}

export function revokeTrustedDevice(deviceId: string) {
  return (dispatch: AppDispatch) =>
    new Promise<{ success: boolean }>((resolve, reject) => {
      dispatch({ type: MFA_TRUSTED_DEVICE_REVOKE_REQUEST });

      httpDelete(`/mfa/trusted-devices/${deviceId}`)
        .then((response: { success: boolean }) => {
          dispatch({
            type: MFA_TRUSTED_DEVICE_REVOKE_SUCCESS,
            payload: { deviceId },
          });
          openFlashMessage(FlashDefinition.trustedDevicesRevoked);
          resolve(response);
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_TRUSTED_DEVICE_REVOKE_ERROR });
          openFlashMessage(FlashDefinition.trustedDevicesRevokeError);
          reject(error);
        });
    });
}

export function revokeAllTrustedDevices() {
  return (dispatch: AppDispatch) =>
    new Promise<{ success: boolean }>((resolve, reject) => {
      dispatch({ type: MFA_TRUSTED_DEVICES_REVOKE_ALL_REQUEST });

      httpDelete('/mfa/trusted-devices')
        .then((response: { success: boolean }) => {
          dispatch({ type: MFA_TRUSTED_DEVICES_REVOKE_ALL_SUCCESS });
          openFlashMessage(FlashDefinition.trustedDevicesAllRevoked);
          resolve(response);
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_TRUSTED_DEVICES_REVOKE_ALL_ERROR });
          openFlashMessage(FlashDefinition.trustedDevicesRevokeError);
          reject(error);
        });
    });
}

export function resetUserMFA(userId: string, reason: string) {
  return (dispatch: AppDispatch) =>
    new Promise<unknown>((resolve, reject) => {
      dispatch({ type: MFA_USER_RESET_REQUEST });

      httpPost(`/mfa/users/${userId}/reset`, { reason })
        .then((response: unknown) => {
          dispatch({
            type: MFA_USER_RESET_SUCCESS,
            payload: { userId },
          });
          openFlashMessage(FlashDefinition.removeUserMFASuccess);
          resolve(response);
        })
        .catch((error: ApiError) => {
          dispatch({ type: MFA_USER_RESET_ERROR });
          openFlashMessage(FlashDefinition.removeUserMFAError);
          reject(error);
        });
    });
}

export function completeMFAChallenge(
  code: string,
  sessionId: string,
  trustDevice?: boolean
) {
  return (dispatch: AppDispatch) =>
    new Promise<MFAChallengeResponse>((resolve, reject) => {
      dispatch(openPageLoading());

      const payload = {
        code,
        session_token: sessionId,
        ...(trustDevice !== undefined && { trust_device: trustDevice }),
      };

      httpPost('/sessions/mfa', payload)
        .then((response: MFAChallengeResponse) => {
          dispatch(closePageLoading());

          const { jwt, exp, user, company } = response;
          const trustedDeviceToken = response.extra_data?.trusted_device_token;

          localStorage.setItem('phoenixAuthToken', jwt);
          localStorage.setItem('phoenixExpToken', exp);
          if (trustedDeviceToken) {
            localStorage.setItem('mfaTrustedDeviceToken', trustedDeviceToken);
          }

          dispatch({
            type: MFA_LOGIN_SUCCESS,
            ...response,
          });

          dispatch({ type: MFA_IN_PROGRESS_END });

          setUpAndJoinDefaultChannels(dispatch, user, company);

          const storedRedirectPath = sessionStorage.getItem('mfaRedirectPath');
          if (storedRedirectPath) {
            sessionStorage.removeItem('mfaRedirectPath');
          }

          handlePostLoginRedirect({
            dispatch,
            user,
            company,
            redirectPath: storedRedirectPath || '',
            isAdminImpersonation: false,
          });

          resolve(response);
        })
        .catch((error: ApiError) => {
          dispatch(closePageLoading());

          if (
            error.response?.status === 410 ||
            error.response?.data?.details === 'session_expired'
          ) {
            clearAuthAndRedirect('session_expired');
            reject(error);
            return;
          }

          dispatch({ type: MFA_LOGIN_ERROR });

          if (error.response?.data?.error === 'account_locked') {
            openFlashMessage(FlashDefinition.accountLocked);
          } else {
            openFlashMessage(FlashDefinition.invalidCode);
          }

          reject(error);
        });
    });
}
